import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Input,
  Button,
  IconButton,
  Sheet,
} from '@mui/joy';
import { ChevronLeft, ChevronRight, Trash2, Plus, Pencil, Check, X } from 'lucide-react';
import { authFetch } from '../../utils/authFetch';
import { toast } from 'react-toastify';
import ModaleConferma from '../../components/ModaleConferma';

export default function Conventions() {
  const selectedOrg = localStorage.getItem('selectedOrg') || '1';

  const [conventions, setConventions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 12;

  // create inline
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [creatingLoading, setCreatingLoading] = useState(false);

  // edit inline
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [savingId, setSavingId] = useState(null);

  // delete confirm
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [conventionToDelete, setConventionToDelete] = useState(null);

  // FETCH CONVENTIONS (con vehicles_count)
  const fetchConventions = useCallback(async () => {
    if (!selectedOrg) return;

    setLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/convention`,
        { credentials: 'include' }
      );
      if (!res.ok) throw new Error('Errore fetch convenzioni');

      const data = await res.json();
      setConventions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Errore nel caricamento convenzioni', { position: 'top-right' });
      setConventions([]);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg]);

  useEffect(() => {
    fetchConventions();
  }, [fetchConventions]);

  // SEARCH
  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return conventions.filter((c) => (c.name || '').toLowerCase().includes(term));
  }, [conventions, searchTerm]);

  // PAGINATION
  const paginated = useMemo(() => {
    const start = (page - 1) * perPage;
    return filtered.slice(start, start + perPage);
  }, [filtered, page]);

  const totalPages = Math.ceil(filtered.length / perPage);

  // EDIT
  const startEdit = (conv) => {
    setEditingId(conv.id);
    setEditName(conv.name || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
  };

  const confirmEdit = async () => {
    if (!editingId) return;
    const name = editName.trim();
    if (!name) {
      toast.error('Il nome convenzione non può essere vuoto', { position: 'top-right' });
      return;
    }

    setSavingId(editingId);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/convention/${editingId}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Errore salvataggio convenzione');
      }

      toast.success('Convenzione aggiornata', { position: 'top-right', autoClose: 2500 });

      setConventions((prev) => prev.map((c) => (c.id === editingId ? { ...c, name } : c)));
      cancelEdit();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Errore nel salvataggio', { position: 'top-right' });
    } finally {
      setSavingId(null);
    }
  };

  // DELETE
  const handleConfirmDelete = async () => {
    const id = conventionToDelete?.id;
    if (!id) return;

    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/convention/${id}`,
        { method: 'DELETE', credentials: 'include' }
      );
      if (!res.ok) throw new Error('Errore eliminazione convenzione');

      toast.success('Convenzione eliminata', { position: 'top-right', autoClose: 2500 });
      setConventions((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
      toast.error('Errore eliminazione convenzione', { position: 'top-right', autoClose: 3000 });
    } finally {
      setConfirmDeleteOpen(false);
      setConventionToDelete(null);
    }
  };

  // CREATE
  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) {
      toast.error('Inserisci un nome convenzione', { position: 'top-right' });
      return;
    }

    setCreatingLoading(true);
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_SERVER_HOST}/org/${selectedOrg}/convention`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name }),
        }
      );

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Errore creazione convenzione');
      }

      const created = await res.json().catch(() => null);

      toast.success('Convenzione creata', { position: 'top-right', autoClose: 2500 });

      if (created && created.id) {
        setConventions((prev) => [created, ...prev]);
      } else {
        await fetchConventions();
      }

      setNewName('');
      setCreating(false);
      setPage(1);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Errore creazione convenzione', { position: 'top-right' });
    } finally {
      setCreatingLoading(false);
    }
  };

  return (
    <Box>
      <Typography level="h3" mb={2}>
        Convenzioni
      </Typography>

      <Box
        mb={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={2}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <Input
            placeholder="Cerca convenzione..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            sx={{ maxWidth: 300 }}
          />
          <Typography level="body-sm" color="neutral">
            {filtered.length} record trovati
          </Typography>
        </Box>

        <Button
          variant="solid"
          color="primary"
          startDecorator={<Plus size={16} />}
          onClick={() => setCreating((v) => !v)}
        >
          Nuova convenzione
        </Button>
      </Box>

      {creating && (
        <Sheet
          variant="outlined"
          sx={{
            p: 2,
            borderRadius: 'lg',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome convenzione (es. 118)"
            sx={{ minWidth: 260 }}
            disabled={creatingLoading}
          />
          <Button onClick={handleCreate} loading={creatingLoading}>
            Crea
          </Button>
          <Button
            variant="soft"
            onClick={() => {
              setCreating(false);
              setNewName('');
            }}
            disabled={creatingLoading}
          >
            Annulla
          </Button>
        </Sheet>
      )}

      {loading ? (
        <Typography>Caricamento...</Typography>
      ) : paginated.length === 0 ? (
        <Typography>Nessuna convenzione trovata.</Typography>
      ) : (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
            >
            {paginated.map((conv) => {
                const isEditing = editingId === conv.id;
                const isSaving = savingId === conv.id;

                return (
                <Sheet
                    key={conv.id}
                    variant="outlined"
                    sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: 'md',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    }}
                >
                    {/* LEFT SIDE */}
                    <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        flexWrap: 'wrap',
                    }}
                    >
                    <Typography level="body-xs" color="neutral">
                        #{conv.id}
                    </Typography>

                    {isEditing ? (
                        <Input
                        size="sm"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        sx={{ minWidth: 200 }}
                        disabled={isSaving}
                        />
                    ) : (
                        <Typography level="title-sm">
                        {conv.name}
                        </Typography>
                    )}

                    <Typography level="body-sm" color="neutral">
                        {conv.vehicles_count ?? 0} mezzi
                    </Typography>
                    </Box>

                    {/* ACTIONS */}
                    <Box display="flex" alignItems="center" gap={1}>
                    {isEditing ? (
                        <>
                        <IconButton
                            size="sm"
                            variant="solid"
                            color="primary"
                            onClick={confirmEdit}
                            disabled={isSaving}
                        >
                            <Check size={16} />
                        </IconButton>

                        <IconButton
                            size="sm"
                            variant="soft"
                            onClick={cancelEdit}
                            disabled={isSaving}
                        >
                            <X size={16} />
                        </IconButton>
                        </>
                    ) : (
                        <>
                        <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => startEdit(conv)}
                        >
                            <Pencil size={16} />
                        </IconButton>

                        <IconButton
                            size="sm"
                            variant="soft"
                            color="danger"
                            disabled={conv.vehicles_count > 0} // opzionale sicurezza
                            onClick={() => {
                            setConventionToDelete(conv);
                            setConfirmDeleteOpen(true);
                            }}
                        >
                            <Trash2 size={16} />
                        </IconButton>
                        </>
                    )}
                    </Box>
                </Sheet>
                );
            })}
            </Box>
      )}

      {totalPages > 1 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Button
            size="sm"
            variant="outlined"
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            startDecorator={<ChevronLeft size={14} />}
          >
            Precedente
          </Button>

          <Typography level="body-sm">
            Pagina {page} di {totalPages}
          </Typography>

          <Button
            size="sm"
            variant="outlined"
            disabled={page === totalPages}
            onClick={() => setPage((p) => p + 1)}
            endDecorator={<ChevronRight size={14} />}
          >
            Successiva
          </Button>
        </Box>
      )}

      <ModaleConferma
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Conferma eliminazione"
        message={
          <>
            Sei sicuro di voler eliminare <b>{conventionToDelete?.name}</b>?
            <br />
            L'azione è irreversibile.
          </>
        }
      />
    </Box>
  );
}